import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router";

import invariant from "tiny-invariant";

import FaceId from "shared/assets/svg/face-id.svg?react";
import { useHolderStore } from "shared/stores/holder-store";
import { useCompletedTasksQuery } from "shared/tg/hooks/use-completed-tasks-query";
import { generateHolderCommitment } from "shared/tg/utils/generate-holder-commitment";
import { getBiometry } from "shared/tg/utils/get-biometry";
import { Img } from "shared/ui/img";
import { catchError } from "shared/utils/catch-error";
import { cn } from "shared/utils/cn";

import { HolderContext } from "./context";

export const HolderProvider = ({ children }: PropsWithChildren) => {
  const [holder, setHolder] = useHolderStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const getHolderCommitmentRef = useRef<Promise<void>>(undefined);

  const completedTasksQuery = useCompletedTasksQuery();

  useEffect(() => {
    if (holder || !completedTasksQuery.isSuccess) return;
    if (!completedTasksQuery.data.length) return;

    const getHolderCommitment = async () => {
      try {
        setIsGenerating(true);
        const { biometry } = await getBiometry();

        invariant(
          biometry.authenticate.isSupported(),
          "biometry.authenticate is not supported"
        );
        const { token } = await biometry.authenticate();
        if (!token) return;

        const holderCommitmentData = await generateHolderCommitment(token);
        const newHolder = { privateKey: token, ...holderCommitmentData };
        setHolder(newHolder);
      } catch (error) {
        catchError(error);
      } finally {
        setIsGenerating(false);
        getHolderCommitmentRef.current = undefined;
      }
    };

    if (!getHolderCommitmentRef.current) {
      getHolderCommitmentRef.current = getHolderCommitment();
    }
  }, [
    holder,
    setHolder,
    setIsGenerating,
    completedTasksQuery.isSuccess,
    completedTasksQuery.data?.length,
  ]);

  if (isGenerating)
    return (
      <div className="flex grow flex-col items-center justify-center">
        <FaceId className="size-20" />
      </div>
    );

  if (!holder && !isGenerating)
    return (
      <div className={cn("flex flex-col items-center justify-center")}>
        <Img alt="no certs" className="max-w-44" />
        <p className="text-balticSea/50 mt-7 max-w-2xs text-center font-medium whitespace-pre-line">
          {`Generate your keys on the home page to unlock ZK Certificates`}
        </p>
      </div>
    );

  return (
    <HolderContext.Provider value={holder}>
      {children ?? <Outlet />}
    </HolderContext.Provider>
  );
};
